'use strict';
import NotificationService from '../ui/Notification';
function preventEventEscape(evt) {
    if (!evt)
        return false;
    evt.preventDefault();
    evt.stopPropagation();
    return false;
}
class DomUtils {
    /**
     * Create a new script tag and insert
     * @param src
     * @param onload
     * @param onerror
     */
    static createScript(src, onload = false, onerror = false) {
        const el = document.createElement('script');
        if (onload)
            el.onload = onload;
        if (onerror)
            el.onerror = onerror;
        el.type = 'text/javascript';
        el.src = src;
        const sibling = document.getElementsByTagName('script')[0];
        sibling?.parentNode?.insertBefore(el, sibling);
    }
    /**
     * Create a new style tag and insert
     * @param src
     * @param onload
     * @param onerror
     */
    static createStyle(src, onload = false, onerror = false) {
        // Create new link Element
        const el = document.createElement('link');
        if (onload)
            el.onload = onload;
        if (onerror)
            el.onerror = onerror;
        el.rel = 'stylesheet';
        el.type = 'text/css';
        el.href = src;
        document.getElementsByTagName('head')[0]?.appendChild(el);
    }
    /**
     * Load a script link onto the page
     * @param src
     * @param asnc
     * @param defer
     * @returns
     */
    static loadScript(src) {
        return new Promise((resolve, reject) => {
            DomUtils.createScript(src, () => resolve(''), err => reject(err));
        });
    }
    /**
     * Load a style link onto the page
     * @param src
     * @returns
     */
    static loadStyle(src) {
        return new Promise((resolve, reject) => {
            DomUtils.createStyle(src, () => resolve(''), err => reject(err));
        });
    }
    /**
     * Add temporary flag attribute and remove after X time
     * @param id
     * @param flag
     * @param remove_after
     * @param remove_after_fn
     * @returns
     */
    static tempFlag(id, flag, remove_after = 200, remove_after_fn = false) {
        let node = document.getElementById(id);
        if (!node)
            return;
        node.setAttribute(`data-${flag}`, '1');
        setTimeout(() => {
            node = document.getElementById(id);
            if (node)
                node.removeAttribute(`data-${flag}`);
            if (remove_after_fn)
                remove_after_fn();
        }, remove_after);
    }
    /**
     * Verify whether or not a dom node has focus
     * @param node
     * @returns
     */
    static hasFocus(node) {
        return document.activeElement === node;
    }
    /**
     * Places the cursor caret at a specific position or at the end of the element
     * @param el
     * @param move_to
     */
    static placeCaret(el, move_to = null) {
        const set_to = move_to ? move_to : el.value.length;
        el.focus();
        el.setSelectionRange(set_to, set_to);
    }
    /**
     * Copy text to the clipboard
     * @param str
     * @param notify
     */
    static copyToClipboard(str, notify = true) {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(str);
        }
        else {
            const el = document.createElement('textarea');
            el.value = str;
            el.setAttribute('readonly', '');
            el.contentEditable = 'true';
            el.style.position = 'absolute';
            el.style.left = '-9999999px';
            document.body.appendChild(el);
            //  Do selection
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(el);
            selection?.removeAllRanges();
            selection?.addRange(range);
            el.setSelectionRange(0, 9999);
            document.execCommand('copy');
            //  Remove element
            document.body.removeChild(el);
        }
        //  Send out notification if wanted
        if (notify) {
            NotificationService.success('notifications.copied_to_clipboard', { icon: 'clipboard-check' });
        }
    }
    /**
     * Download a file
     * @param name
     * @param src
     */
    static downloadFile(name, src) {
        const lnk = document.createElement('a');
        lnk.href = src;
        lnk.download = name;
        //  Trigger link click
        document.body.appendChild(lnk);
        lnk.click();
        document.body.removeChild(lnk);
    }
    /**
     * Selects all children of a specific element
     * @param el
     */
    static selectAll(el) {
        window.getSelection()?.selectAllChildren(el);
    }
    /**
     * Prevent an event from escaping along the dom tree
     * @param evt
     * @returns
     */
    static preventEventEscape(evt) {
        return preventEventEscape(evt);
    }
    /**
     * Prevent an event from immediately escaping along the dom tree
     * @param evt
     * @returns
     */
    static preventImmediateEventEscape(evt) {
        if (!evt)
            return false;
        evt.stopImmediatePropagation();
        return preventEventEscape(evt);
    }
    /**
     * Recreate a dom node
     */
    static recreateNode(el) {
        const clone = el.cloneNode(true);
        el.parentNode?.replaceChild(clone, el);
        return clone;
    }
    /**
     * Clear out all contents of a dom node
     * @param el
     */
    static clearNodeContent(el) {
        while (el.firstChild) {
            if (el.lastChild) {
                el.removeChild(el.lastChild);
            }
        }
    }
    /**
     * Check if a node is before another node (siblings)
     * @param el_1
     * @param el_2
     * @returns
     */
    static isBefore(el_1, el_2) {
        //  If the nodes don't have the same parent -> false
        if (el_1.parentNode !== el_2.parentNode)
            return false;
        for (let cursor = el_1.previousSibling; cursor && cursor.nodeType !== 9; cursor = cursor.previousSibling) {
            if (cursor === el_2)
                return true;
        }
        return false;
    }
    /**
     * Check if the user has asked the OS or browser to reduce motion.
     * Use it to skip JS-driven motion, e.g. pass `behavior: 'auto'` instead of 'smooth' when scrolling.
     * @returns
     */
    static prefersReducedMotion() {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return false;
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
}
export { DomUtils };
export default DomUtils;
//# sourceMappingURL=DomUtils.js.map